import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Database,
  BarChart3,
  Menu,
  Moon,
  Radio,
  Settings,
  ShieldCheck,
  ShieldUser,
  Sun,
  TestTube2,
  Trash2,
  Users,
} from "lucide-react";
import { useAuth } from "../../context/AuthProvider";
import {
  isLegacyHost,
  isLegacyUrlExpired,
  NEW_URL,
  EXPIRES_ON,
} from "../UrlSunsetWarning";
import logo from "../../assets/cynet-systems-squarelogo.png";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Tooltip({ label, children, disabled = false }) {
  if (disabled) return children;
  return (
    <div className="group relative">
      {children}
      <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="px-2 py-1 rounded bg-black text-white text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
          {label}
        </div>
      </div>
    </div>
  );
}

function NavIconButton({ to, icon: Icon, label, disabled = false, expanded = false }) {
  if (disabled) {
    return (
      <Tooltip label={label} disabled={expanded}>
        <div
          className={cx(
            "text-slate-500/60 dark:text-slate-500/60",
            "cursor-not-allowed"
          )}
          aria-disabled="true"
        >
          <div className={cx("p-2 rounded-lg flex items-center gap-3", expanded && "px-3 w-full")}>
            <Icon className="w-5 h-5 flex-shrink-0" />
            {expanded && (
              <span className="text-xs font-black uppercase tracking-widest truncate">
                {label}
              </span>
            )}
          </div>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={label} disabled={expanded}>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cx(
            "transition-colors",
            isActive
              ? "text-indigo-500"
              : "text-slate-500 hover:text-indigo-500 dark:text-slate-500 dark:hover:text-indigo-400"
          )
        }
        aria-label={label}
      >
        {({ isActive }) => (
          <div
            className={cx(
              "p-2 rounded-lg transition-all flex items-center gap-3",
              expanded && "px-3 w-full",
              isActive && "bg-indigo-600/10 dark:bg-indigo-500/10"
            )}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {expanded && (
              <span className="text-xs font-black uppercase tracking-widest truncate">
                {label}
              </span>
            )}
          </div>
        )}
      </NavLink>
    </Tooltip>
  );
}

export default function PortalLayout({ children, headerRight }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminSettingsOpen, setAdminSettingsOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const profileRef = useRef(null);
  const adminSettingsRef = useRef(null);

  useEffect(() => {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const storedTheme = localStorage.getItem("theme");
    const shouldDark = storedTheme === "dark" || (!storedTheme && prefersDark);
    setDarkMode(shouldDark);
    document.documentElement.classList.toggle("dark", shouldDark);
  }, []);

  const toggleDark = () => {
    setDarkMode((prev) => {
      const next = !prev;
      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("theme", next ? "dark" : "light");
      return next;
    });
  };

  const normalizedRole = String(user?.role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const showAdminNav = normalizedRole === "admin" || normalizedRole === "super_admin";
  const showSuperAdminNav = normalizedRole === "super_admin";

  const envLabel = useMemo(() => {
    if (location.pathname.startsWith("/uat")) return "Environment: UAT";
    return "Environment: Production";
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
      if (adminSettingsRef.current && !adminSettingsRef.current.contains(event.target)) {
        setAdminSettingsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Hide portal chrome on login route
  const isAuthRoute = location.pathname.startsWith("/auth/");
  if (isAuthRoute) return children;

  const isInAdmin = location.pathname.startsWith("/admin");
  const isLangSmithRoute = location.pathname.startsWith("/langsmith");

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900 dark:bg-[#0a0a0c] dark:text-slate-200 transition-colors duration-300">
      {/* Slim Integrated Sidebar */}
      <nav
        className={cx(
          "flex-shrink-0 flex flex-col border-r border-slate-200 bg-white dark:border-white/5 dark:bg-black z-50 transition-all duration-300",
          sidebarExpanded ? "w-60" : "w-16"
        )}
      >
        {/* Top aligned with header */}
        <div className={cx(
          "h-14 w-full flex items-center px-2 border-b border-slate-200 dark:border-white/5",
          sidebarExpanded ? "justify-start" : "justify-center"
        )}>
          <Tooltip label={sidebarExpanded ? "Cynet Labs" : "Expand"} disabled={sidebarExpanded}>
            <button
              type="button"
              onClick={() => {
                if (!sidebarExpanded) setSidebarExpanded(true);
              }}
              className={cx(
                "flex items-center gap-3 min-w-0 rounded-lg",
                !sidebarExpanded && "hover:bg-slate-100 dark:hover:bg-white/10 p-1",
                sidebarExpanded && "p-0 cursor-default"
              )}
              aria-label={sidebarExpanded ? "Cynet Labs" : "Expand sidebar"}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white border border-slate-200 dark:bg-white/[0.03] dark:border-white/10 flex-shrink-0">
                <img src={logo} alt="Cynet Labs" className="w-6 h-6 object-contain" />
              </div>
              {sidebarExpanded && (
                <span className="text-xs font-black uppercase tracking-[0.2em] text-slate-800 dark:text-slate-200 truncate">
                  Cynet Labs
                </span>
              )}
            </button>
          </Tooltip>
        </div>

        <div className={cx("flex-1 flex flex-col gap-2 pt-4", sidebarExpanded ? "px-2" : "items-center")}>
          <NavIconButton to="/" icon={Radio} label="Live Projects" expanded={sidebarExpanded} />
          <NavIconButton
            to="/uat"
            icon={TestTube2}
            label="UAT Projects"
            disabled={!showAdminNav}
            expanded={sidebarExpanded}
          />
          {showSuperAdminNav && (
            <NavIconButton
              to="/langsmith"
              icon={BarChart3}
              label="Project Costing"
              expanded={sidebarExpanded}
            />
          )}
          {showAdminNav && (
            <NavIconButton to="/admin" icon={ShieldCheck} label="Admin Dashboard" expanded={sidebarExpanded} />
          )}
          {showAdminNav && isInAdmin && (
            <div className="relative" ref={adminSettingsRef}>
              <Tooltip label="Admin Settings" disabled={sidebarExpanded}>
                <button
                  type="button"
                  onClick={() => setAdminSettingsOpen((v) => !v)}
                  className={cx(
                    "text-slate-500 hover:text-indigo-500 dark:text-slate-500 dark:hover:text-indigo-400 transition-colors w-full",
                    !sidebarExpanded && "flex justify-center"
                  )}
                  aria-label="Admin settings"
                >
                  <div
                    className={cx(
                      "p-2 rounded-lg transition-all flex items-center gap-3",
                      sidebarExpanded && "px-3 w-full",
                      adminSettingsOpen && "bg-indigo-600/10 dark:bg-indigo-500/10"
                    )}
                  >
                    <Settings className="w-5 h-5" />
                    {sidebarExpanded && (
                      <span className="text-xs font-black uppercase tracking-widest truncate">
                        Admin Settings
                      </span>
                    )}
                  </div>
                </button>
              </Tooltip>

              {adminSettingsOpen && (
                <div className="absolute left-full top-0 ml-3 w-64 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 dark:bg-black dark:border-white/10">
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-white/10">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-500">
                      Settings
                    </p>
                  </div>
                  <div className="p-2">
                    <NavLink
                      to="/admin/users"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                      onClick={() => setAdminSettingsOpen(false)}
                    >
                      <Users className="w-4 h-4" />
                      Manage Team
                    </NavLink>
                    <NavLink
                      to="/admin/devfolio-users"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                      onClick={() => setAdminSettingsOpen(false)}
                    >
                      <ShieldUser className="w-4 h-4" />
                      Devfolio User Access
                    </NavLink>
                    <NavLink
                      to="/admin/trash"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                      onClick={() => setAdminSettingsOpen(false)}
                    >
                      <Trash2 className="w-4 h-4" />
                      Trash
                    </NavLink>
                    <NavLink
                      to="/admin/migration"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                      onClick={() => setAdminSettingsOpen(false)}
                    >
                      <Database className="w-4 h-4" />
                      DB Migration
                    </NavLink>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom controls (toggle, profile, theme) */}
        <div className={cx("pb-3", sidebarExpanded ? "px-2" : "flex flex-col items-center")}>
          <Tooltip label={sidebarExpanded ? "Collapse" : "Expand"} disabled={sidebarExpanded}>
            <button
              type="button"
              onClick={() => setSidebarExpanded((v) => !v)}
              className={cx(
                "rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition text-slate-500 mb-2",
                sidebarExpanded ? "w-full px-3 py-2 flex items-center gap-3" : "p-2.5"
              )}
              aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
              title={sidebarExpanded ? "Collapse" : "Expand"}
            >
              <Menu className="w-6 h-6 flex-shrink-0" />
              {sidebarExpanded && (
                <span className="text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-400">
                  {sidebarExpanded ? "Collapse" : "Expand"}
                </span>
              )}
            </button>
          </Tooltip>

          {/* Profile + Logout (Sidebar) */}
          {user && (
            <div className={cx("relative mb-2", sidebarExpanded ? "w-full" : "flex justify-center")} ref={profileRef}>
              <Tooltip label="Profile" disabled={sidebarExpanded}>
                <button
                  onClick={() => setProfileOpen((prev) => !prev)}
                  className={cx(
                    "rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition",
                    sidebarExpanded ? "w-full px-3 py-2 flex items-center gap-3" : "p-2.5"
                  )}
                  aria-label="Profile menu"
                >
                  <img
                    src={user.photo || user.photoURL || "https://ui-avatars.com/api/?name=User"}
                    alt={user.displayName || user.email || "User"}
                    className="w-9 h-9 rounded-full border border-slate-200 dark:border-white/10 flex-shrink-0"
                  />
                  {sidebarExpanded && (
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">
                        {user.displayName || user.name || "User"}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                        {user.email}
                      </div>
                    </div>
                  )}
                </button>
              </Tooltip>

              {profileOpen && (
                <div
                  className={cx(
                    "absolute w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 dark:bg-black dark:border-white/10",
                    sidebarExpanded ? "left-0 bottom-full mb-2" : "left-full bottom-0 ml-3"
                  )}
                >
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-white/10">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {user.displayName || user.name || "User"}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {user.email}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await logout();
                      setProfileOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}

          <Tooltip label="Theme" disabled={sidebarExpanded}>
            <button
              onClick={toggleDark}
              className={cx(
                "rounded-lg transition-all",
                sidebarExpanded ? "w-full px-3 py-2 flex items-center gap-3" : "p-2.5",
                darkMode ? "text-yellow-400" : "text-slate-400",
                "hover:bg-slate-100 dark:hover:bg-white/10"
              )}
              aria-label="Toggle theme"
            >
              {darkMode ? <Sun className="w-6 h-6 flex-shrink-0" /> : <Moon className="w-6 h-6 flex-shrink-0" />}
              {sidebarExpanded && (
                <span className="text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-400">
                  Theme
                </span>
              )}
            </button>
          </Tooltip>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Compact Header */}
        <header className="px-6 h-14 border-b flex items-center justify-between border-slate-200 bg-white dark:border-white/5 dark:bg-black">
          <div className="flex items-center gap-6 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="text-lg font-black uppercase tracking-[0.2em] truncate">
                Cynet Labs
              </h1>
            </div>
            {isLegacyHost() && !isLegacyUrlExpired() ? (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-400/15 rounded min-w-0">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase truncate">
                  This link expires on {EXPIRES_ON} — use{" "}
                  <a href={NEW_URL} className="underline">
                    labs.cynetcorp.com
                  </a>
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/10 rounded">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-black text-emerald-500 uppercase">
                  Explore Cynet’s Project Showcase
                </span>
              </div>
            )}
            {isLangSmithRoute && (
              <div className="hidden lg:block min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  Project Costing
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                  Monitor token usage & cost across all projects in real-time.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">{headerRight}</div>
        </header>

        {isLegacyHost() && !isLegacyUrlExpired() && (
          <div className="px-6 py-2 bg-amber-400 text-black text-center text-sm font-semibold">
            This link expires on {EXPIRES_ON}. Please use{" "}
            <a href={NEW_URL} className="underline font-bold">
              {NEW_URL}
            </a>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">{children}</div>

        {/* Integrated Bottom Status Bar */}
        <footer className="h-8 px-6 border-t flex items-center justify-between text-[10px] font-bold uppercase tracking-widest bg-slate-100 border-slate-200 text-slate-400 dark:bg-black dark:border-white/5 dark:text-slate-600">
          <div className="flex gap-6">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
              {envLabel}
            </span>
          </div>
          <div className="text-[10px] font-bold tracking-widest uppercase">
            Copyright © Cynet Systems. All rights reserved.
          </div>
        </footer>
      </main>
    </div>
  );
}

